import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import AnalyticsCard from '../AnalyticsCard';
import { Loader } from 'ncoded-component-library';
import LeadActivityEmptyState from './components/LeadActivityEmptyState';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import { ResponsivePie } from '@nivo/pie';
import { INNER_RADIUS, LEAD_ACTIVITY_SEGMENTS, MARGINS } from './data';
import utils, {
  formatPercentage,
  getArrayLength,
  getHasElementsInArray,
} from 'utils';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import useQueryParams from 'hooks/useQueryParams';
import DealsFilter from './components/DealsFilter';
import { FIRST_PAGE, MAX_LIMIT } from 'data/global';
import { DealRoom } from 'models/DealRoom';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './ActiveLeadsCard.styles.scss';
import './ActiveLeadsCard.styles.responsive.scss';

const LEAD_ACTIIVTY_FILTER_KEY = 'leadActivityDealRoomId';

const getRoundedPercentage = (percentage = 0) => Number(percentage.toFixed(1));

type LeadActivityStat = {
  id: string;
  label: string;
  color: string;
  infoKey?: string;
  count: number;
  value: number;
  percentage: number;
};

type ActiveLeadsCardProps = {
  className?: string;
};

const ActiveLeadsCard: React.FC<ActiveLeadsCardProps> = (props) => {
  const { className } = props;

  const [isLoadingDeals, setIsLoadingDeals] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [leadsActivityStats, setLeadsActivityStats] = useState<
    Array<LeadActivityStat>
  >([]);
  const [deals, setDeals] = useState<DealRoom[]>([]);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { enterprise } = useContext(EnterpriseContext);

  const { params } = useQueryParams();

  const leadActivityDealRoomId = params[LEAD_ACTIIVTY_FILTER_KEY];

  const isLoading = isLoadingDeals || isLoadingStats;

  const hasDeals = getHasElementsInArray(deals);

  // The tooltips talk about "the selected deal(s)", so they follow the filter:
  // a single deal when one is picked, every deal the viewer has otherwise.
  const selectedDealsCount = leadActivityDealRoomId ? 1 : getArrayLength(deals);

  const totalLeads = useMemo(
    () => leadsActivityStats.reduce((total, { count }) => total + count, 0),
    [leadsActivityStats],
  );

  // Only the segments that actually have leads are drawn, so the donut never
  // renders zero width arcs. The legend keeps all of them.
  const chartData = useMemo(
    () => leadsActivityStats.filter(({ count }) => !!count),
    [leadsActivityStats],
  );

  const classes = classNames('active-leads-card', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const filterOptions = useMemo(
    () => deals.map(({ _id, name }) => ({ label: name, value: _id })),
    [deals],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingDeals(true);

        const {
          data: { items },
        } = await api.enterpriseAnalytics.getAllDeals(enterprise?._id, {
          $page: FIRST_PAGE,
          $limit: MAX_LIMIT,
          onlyMyDeals: !isSuperAdmin,
        });

        setDeals(items);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoadingDeals(false);
      }
    })();
  }, [enterprise?._id, getMixpanelProps, isSuperAdmin]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingStats(true);

        const { data } = await api.enterpriseAnalytics.getLeadActivity(
          enterprise?._id,
          leadActivityDealRoomId || undefined,
        );

        setLeadsActivityStats(
          LEAD_ACTIVITY_SEGMENTS.map(({ status, color, infoKey }) => {
            const { count = 0, percentage = 0 } =
              data?.find((item) => item.status === status) || {};

            return {
              id: status,
              label: t(status, { count: selectedDealsCount }),
              color,
              infoKey,
              count,
              // The arcs are sized by the lead count, the percentage is only
              // rendered in the legend.
              value: count,
              percentage: getRoundedPercentage(percentage),
            };
          }),
        );
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoadingStats(false);
      }
    })();
  }, [
    enterprise?._id,
    getMixpanelProps,
    leadActivityDealRoomId,
    selectedDealsCount,
    t,
  ]);

  const renderContent = useMemo(() => {
    if (isLoading) return <Loader />;

    if (!hasDeals)
      return (
        <LeadActivityEmptyState
          className="active-leads-card__empty-state"
          title={t('noDealsYet')}
          description={t('createDealToSeeLeadActivity')}
        />
      );

    if (!totalLeads)
      return (
        <LeadActivityEmptyState
          className="active-leads-card__empty-state"
          title={t('noLeadsOnSelectedDealsYet')}
          description={t('activityWillAppearOnceLeadsAreAdded')}
        />
      );

    return (
      <div className="active-leads-card__graph">
        <div className="chart-wrapper">
          <ResponsivePie
            data={chartData}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            isInteractive={false}
            colors={{ datum: 'data.color' }}
            margin={MARGINS}
            innerRadius={INNER_RADIUS}
          />

          <div className="chart-total">
            <p>{utils.getDotedNumber(totalLeads)}</p>
            <span>{t('lead', { count: totalLeads })}</span>
          </div>
        </div>

        <div className="chart-leggend">
          {leadsActivityStats.map(
            ({ id, label, color, infoKey, count, percentage }) => (
              <div key={id} className="chart-leggend__item">
                <span
                  className="chart-leggend__item__dot"
                  style={{ backgroundColor: color }}
                />
                <div>
                  <div className="chart-leggend__item__label">
                    {label}
                    {infoKey && (
                      <ResolvePopup
                        onHover
                        popupContent={t(infoKey, { count: selectedDealsCount })}
                        className="chart-leggend__item__info"
                      >
                        <InformationcircleIcon />
                      </ResolvePopup>
                    )}
                  </div>
                  <p>
                    {utils.getDotedNumber(count)}
                    <span>{`(${formatPercentage(percentage)})`}</span>
                  </p>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    );
  }, [
    chartData,
    hasDeals,
    isLoading,
    leadsActivityStats,
    selectedDealsCount,
    t,
    totalLeads,
  ]);

  return (
    <AnalyticsCard
      className={classes}
      title={t('leadActivity')}
      action={
        <DealsFilter
          filterKey={LEAD_ACTIIVTY_FILTER_KEY}
          options={filterOptions}
        />
      }
    >
      {renderContent}

      <p className="description">{t('activeLeadsDescription')}</p>
    </AnalyticsCard>
  );
};

export default ActiveLeadsCard;
